import { useState, useCallback, useEffect } from 'react';
import { useWebSocket } from './useWebSocket';
import toast from 'react-hot-toast';

/**
 * Hook to manage crawl updates via WebSocket
 * Handles all crawl-related real-time events
 */
export const useCrawlUpdates = () => {
  const [crawlStatuses, setCrawlStatuses] = useState({}); // placeId -> status
  const [linksByPlace, setLinksByPlace] = useState({}); // placeId -> links[]
  const [crawlProgress, setCrawlProgress] = useState({}); // placeId -> progress
  const [batchProgress, setBatchProgress] = useState({}); // batchId -> progress

  const handleWebSocketMessage = useCallback((data) => {
    const { type } = data;

    switch (type) {
      case 'connection_established':
        console.log('Connected to real-time crawl updates');
        // REMOVED: toast notification
        break;

      case 'auto_crawl_triggered':
        console.log('Auto-crawl triggered:', data.place_name);
        // REMOVED: toast notification
        
        setCrawlStatuses(prev => ({
          ...prev,
          [data.place_id]: {
            status: 'pending',
            crawl_task_id: data.crawl_task_id,
            trigger_reason: data.trigger_reason
          }
        }));
        break;

      case 'crawl_started':
        console.log('Crawl started:', data.place_name);
        
        setCrawlStatuses(prev => ({
          ...prev,
          [data.place_id]: {
            status: 'processing',
            crawl_task_id: data.crawl_task_id,
            place_name: data.place_name
          }
        }));

        setCrawlProgress(prev => ({
          ...prev,
          [data.place_id]: {
            percentage: 0,
            current_step: 'Starting...'
          }
        }));
        break;

      case 'crawl_progress':
        console.log('Crawl progress:', data.current_step);
        
        setCrawlProgress(prev => ({
          ...prev,
          [data.place_id]: {
            percentage: data.progress_percentage || 0,
            current_step: data.current_step,
            completed_steps: data.completed_steps,
            total_steps: data.total_steps
          }
        }));
        break;

      case 'link_discovered':
        console.log('Link discovered:', data.link.link_type, 'for place', data.place_id);
        
        // Add new link to place's links
        setLinksByPlace(prev => ({
          ...prev,
          [data.place_id]: [...(prev[data.place_id] || []), data.link]
        }));

        // REMOVED: toast notification
        break;

      case 'social_links_count_updated':
        console.log('Social links count updated for place:', data.place_id);
        // This can be used to update badge counts
        break;

      case 'crawl_completed':
        console.log('Crawl completed:', data.place_name, 'Links found:', data.links_found);
        
        setCrawlStatuses(prev => ({
          ...prev,
          [data.place_id]: {
            status: 'completed',
            crawl_task_id: data.crawl_task_id,
            place_name: data.place_name,
            links_found: data.links_found,
            duration: data.duration
          }
        }));

        setCrawlProgress(prev => {
          const newProgress = { ...prev };
          delete newProgress[data.place_id];
          return newProgress;
        });

        // REMOVED: toast notification
        break;

      case 'crawl_failed':
        console.error('Crawl failed:', data.place_name, data.error_message);
        
        setCrawlStatuses(prev => ({
          ...prev,
          [data.place_id]: {
            status: 'failed',
            crawl_task_id: data.crawl_task_id,
            place_name: data.place_name,
            error_message: data.error_message,
            can_retry: data.can_retry
          }
        }));

        setCrawlProgress(prev => {
          const newProgress = { ...prev };
          delete newProgress[data.place_id];
          return newProgress;
        });

        // REMOVED: toast notification
        break;

      case 'crawl_status_changed':
        console.log('Crawl status changed:', data.place_id, data.new_status);
        
        setCrawlStatuses(prev => ({
          ...prev,
          [data.place_id]: {
            ...prev[data.place_id],
            status: data.new_status,
            old_status: data.old_status,
            can_recrawl: data.can_recrawl,
            recrawl_reason: data.recrawl_reason
          }
        }));
        break;

      case 'batch_crawl_started':
        console.log('Batch crawl started:', data.batch_id, 'Total places:', data.total_places);
        
        setBatchProgress(prev => ({
          ...prev,
          [data.batch_id]: {
            total_places: data.total_places,
            completed: 0,
            percentage: 0,
            place_ids: data.place_ids
          }
        }));

        toast.info(
          `Starting batch crawl for ${data.total_places} place${data.total_places !== 1 ? 's' : ''}`,
          { 
            icon: '🚀',
            duration: 3000 
          }
        );
        break;

      case 'batch_crawl_progress':
        console.log('Batch progress:', data.completed_count, '/', data.total_count);
        
        setBatchProgress(prev => ({
          ...prev,
          [data.batch_id]: {
            ...prev[data.batch_id],
            completed: data.completed_count,
            percentage: data.progress_percentage,
            current_place: data.current_place
          }
        }));
        break;

      case 'batch_crawl_completed':
        console.log('Batch crawl completed:', data.batch_id);
        
        setBatchProgress(prev => {
          const newProgress = { ...prev };
          delete newProgress[data.batch_id];
          return newProgress;
        });

        toast.success(
          `Batch crawl completed! ${data.successful}/${data.total_places} successful, ${data.total_links_found} links found`,
          { 
            icon: '🎉',
            duration: 5000 
          }
        );
        break;

      case 'pong':
        // Connection health check response
        break;

      default:
        console.log('Unknown WebSocket message type:', type);
    }
  }, []);

  const { isConnected, connectionError, send, subscribePlaces, unsubscribePlaces } = 
    useWebSocket(handleWebSocketMessage);

  // Clear status for a place
  const clearPlaceStatus = useCallback((placeId) => {
    setCrawlStatuses(prev => {
      const newStatuses = { ...prev };
      delete newStatuses[placeId];
      return newStatuses;
    });

    setCrawlProgress(prev => {
      const newProgress = { ...prev };
      delete newProgress[placeId];
      return newProgress;
    });
  }, []);

  // Get status for a specific place
  const getPlaceStatus = useCallback((placeId) => {
    return crawlStatuses[placeId] || null;
  }, [crawlStatuses]);

  // Get progress for a specific place
  const getPlaceProgress = useCallback((placeId) => {
    return crawlProgress[placeId] || null;
  }, [crawlProgress]);

  // Get links for a specific place
  const getPlaceLinks = useCallback((placeId) => {
    return linksByPlace[placeId] || [];
  }, [linksByPlace]);

  return {
    isConnected,
    connectionError,
    crawlStatuses,
    crawlProgress,
    batchProgress,
    linksByPlace,
    subscribePlaces,
    unsubscribePlaces,
    clearPlaceStatus,
    getPlaceStatus,
    getPlaceProgress,
    getPlaceLinks,
    send
  };
};

export default useCrawlUpdates;